/**
 * 55 — the front door.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  This is the first thing four non-technical people ever see, and two of them
 *  own a phone and no computer. It is TWO TAPS: tap your name, tap in the PIN.
 *  There is no email, no password, no round-trip to an inbox, no "Go" button to
 *  hunt for. The cookie lasts a year, so they do this once, ever, per phone.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Two decisions worth defending:
 *
 *   · The keypad is drawn in the DOM, not summoned from the OS. A phone's soft
 *     numeric keyboard covers half the screen, offers autocorrect on a number,
 *     and on iOS will happily suggest a saved credit card. Twelve big round
 *     buttons we control are strictly better, and they work with a real
 *     keyboard too (see onKey).
 *
 *   · It auto-submits on the sixth digit. A submit button is one more thing to
 *     find, and the only thing it could ever mean is "I have typed six digits",
 *     which we already know.
 *
 * It imports nothing from api.ts at runtime — only the `Member` type, which is
 * erased at build. The login screen has to work when it is the ONLY thing that
 * works, so it owns its four lines of fetch.
 */
import type { Member } from './api';
import { clear, h } from './ui';
import { createVinyl } from './vinyl';

/* ---------------------------------------------------------------- contract */

const PIN_LENGTH = 6;

/**
 * A member as the login screen needs them: the short name in big type, the
 * instrument underneath, the colour as the accent. `instrument` comes straight
 * from members.instrument; if the Worker ever stops sending it, the pill still
 * says the name, which is the part that matters.
 */
export interface LoginMember extends Member {
  instrument?: string | null;
  full_name?: string | null;
  sort?: number | null;
}

/**
 * The endpoints. Both are lists because the PIN vault landed in a parallel
 * branch: a 404 on the first name falls through to the second, and NOTHING else
 * does — a 401 is a wrong PIN and must never be retried against another route.
 * A login screen that cannot log anybody in is the worst failure this app has.
 */
const MEMBER_PATHS = ['/api/auth/members', '/api/members'];
const LOGIN_PATHS = ['/api/auth/login', '/api/auth/pin'];

class LoginError extends Error {
  readonly status: number;
  readonly code: string;
  /** Only ever set on a 429. Seconds, from the Worker. */
  readonly retryAfterS: number | null;

  constructor(status: number, code: string, retryAfterS: number | null = null) {
    super(code);
    this.name = 'LoginError';
    this.status = status;
    this.code = code;
    this.retryAfterS = retryAfterS;
  }
}

/**
 * Dev only. `wrangler dev` has no cookie jar and no Access, so the Worker's
 * identity stub reads ?dev_email=. We pass it ONLY when it has been asked for
 * explicitly — otherwise you could never see this screen on localhost, which is
 * a fine way to ship a broken front door. `?login` forces it on top of that.
 */
function devUrl(path: string): string {
  if (!import.meta.env.DEV) return path;
  const email =
    new URLSearchParams(location.search).get('dev_email') ?? localStorage.getItem('55.dev_email');
  if (!email) return path;
  return path + (path.includes('?') ? '&' : '?') + 'dev_email=' + encodeURIComponent(email);
}

async function call<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(devUrl(path), {
      method: body === undefined ? 'GET' : 'POST',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // The session cookie the Worker sets. Same origin: the API is /api/* on
      // this very domain.
      credentials: 'same-origin',
      signal,
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new LoginError(0, navigator.onLine ? 'NETWORK' : 'OFFLINE');
  }

  if (!res.ok) {
    let code = 'HTTP_' + res.status;
    let retry: number | null = null;
    try {
      const j = (await res.json()) as { code?: string; retry_after_s?: number; retry_after?: number };
      if (j.code) code = j.code;
      const r = j.retry_after_s ?? j.retry_after;
      if (typeof r === 'number' && Number.isFinite(r) && r > 0) retry = r;
    } catch {
      /* an error with no JSON body is still an error; we just have less to go on */
    }
    if (retry === null) {
      const hdr = Number(res.headers.get('retry-after'));
      if (Number.isFinite(hdr) && hdr > 0) retry = hdr;
    }
    throw new LoginError(res.status, code, retry);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Try each path in turn. Only a 404/405 — "no such route" — moves on. */
async function callFirst<T>(paths: string[], body?: unknown, signal?: AbortSignal): Promise<T> {
  let last: unknown;
  for (const p of paths) {
    try {
      return await call<T>(p, body, signal);
    } catch (e) {
      last = e;
      const gone = e instanceof LoginError && (e.status === 404 || e.status === 405);
      if (!gone) throw e;
    }
  }
  throw last;
}

/** `{ member: {…} }` or a bare member. Be generous in what you accept. */
function asMember(raw: unknown): LoginMember | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const m = (o['member'] ?? o) as Record<string, unknown>;
  return typeof m['id'] === 'string' && typeof m['name'] === 'string'
    ? (m as unknown as LoginMember)
    : null;
}

/** `{ members: [...] }` or a bare array, in the band's own order. */
function asMembers(raw: unknown): LoginMember[] {
  const arr = Array.isArray(raw)
    ? raw
    : ((raw as { members?: unknown } | null)?.members ?? []);
  if (!Array.isArray(arr)) return [];
  const out = arr.map(asMember).filter((m): m is LoginMember => m !== null);
  out.sort((a, b) => (a.sort ?? 99) - (b.sort ?? 99));
  return out;
}

/**
 * GET /api/me. Null when there is no session — which is the ONLY question the
 * caller is asking, so a 500 or a dead network answers it the same way: show
 * the front door. It never throws: an unhandled rejection here is a black
 * screen on a phone in a pub, and the login screen itself says the offline part
 * out loud and offers a Try again.
 */
export async function currentMember(): Promise<Member | null> {
  // Dev: `?login` forces the front door even though the Worker's dev stub is
  // perfectly happy to hand you an identity.
  if (import.meta.env.DEV && new URLSearchParams(location.search).has('login')) return null;
  try {
    return asMember(await call<unknown>('/api/me'));
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------- words */

/** Never a status code. Never "401". (06_FRONTEND_SPEC) */
function vaultMessage(e: unknown): string {
  const code = e instanceof LoginError ? e.code : '';
  if (code === 'OFFLINE') return "You're offline. Try again when you've got signal.";
  return "Couldn't reach the vault. Try again?";
}

function minutesFrom(seconds: number): string {
  const m = Math.max(1, Math.ceil(seconds / 60));
  return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
}

/** A short buzz on Android. iOS ignores it, and that is fine. */
function buzz(ms: number): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* a phone that won't buzz is not an error */
  }
}

const REDUCED = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const BACKSPACE_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M21 5H9L3 12l6 7h12z"/><path d="M17 9.5l-5 5M12 9.5l5 5"/></svg>`;

/* ------------------------------------------------------------------- screen */

/**
 * Draw the front door. Returns its own teardown, like every other screen.
 * `onDone` fires exactly once, with the member the Worker says you are.
 */
export function mountLogin(root: HTMLElement, onDone: (me: Member) => void): () => void {
  clear(root);
  // Hides the tab bar and the now-playing bar while the door is shut. A class,
  // not an inline style, so main.ts's own show/hide logic is never fought with.
  document.body.classList.add('is-login');

  const ac = new AbortController();

  let dead = false;
  let done = false;
  let stage: 'pick' | 'pin' = 'pick';
  let picked: LoginMember | null = null;
  let pin = '';
  let busy = false;
  /** epoch ms; > now means the Worker has told us to wait (429) */
  let lockedUntil = 0;
  let lockTimer = 0;
  let okTimer = 0;

  /* ------------------------------------------------------------ the frame */

  const mark = createVinyl({ arm: false });
  mark.setPlaying(true); // it turns on its own. The room is warming up.

  const say = h('p', { class: 'login-say', text: 'Tap your name.' });
  const pills = h('div', { class: 'login-pills', role: 'group', 'aria-label': 'Who are you?' });

  const dots = h(
    'div',
    { class: 'pin-dots', role: 'img', 'aria-label': `0 of ${PIN_LENGTH} digits entered` },
    Array.from({ length: PIN_LENGTH }, () => h('i', { class: 'pin-dot' })),
  );
  const dotEls = [...dots.querySelectorAll<HTMLElement>('.pin-dot')];

  const msg = h('p', { class: 'pin-msg', role: 'status', 'aria-live': 'polite' });

  const keypad = h('div', { class: 'pin-keys' });
  const keys: HTMLButtonElement[] = [];

  for (const label of ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫']) {
    if (label === '') {
      keypad.appendChild(h('span', { class: 'pin-gap', 'aria-hidden': 'true' }));
      continue;
    }
    const isBack = label === '⌫';
    const key = h('button', {
      class: 'pin-key' + (isBack ? ' back' : ''),
      type: 'button',
      'aria-label': isBack ? 'Delete the last digit' : label,
      ...(isBack ? { html: BACKSPACE_SVG } : { text: label }),
    }) as HTMLButtonElement;
    key.addEventListener('click', () => (isBack ? rub() : press(label)));
    keys.push(key);
    keypad.appendChild(key);
  }

  const notYou = h('button', { class: 'pin-back', type: 'button', text: 'Not you?' });
  notYou.addEventListener('click', unpick);

  const pad = h('section', { class: 'login-pad', 'aria-label': 'Enter the PIN', hidden: true }, [
    dots,
    msg,
    keypad,
    notYou,
  ]);

  const el = h('div', { class: 'login', 'data-stage': 'pick' }, [
    h('div', { class: 'login-top' }, [
      h('div', { class: 'login-mark' }, [mark.el]),
      h('h1', { class: 'login-word', text: '2026' }),
      say,
    ]),
    pills,
    pad,
    // The whole reason this screen is bearable: they only ever do it once.
    h('p', { class: 'login-foot', text: "You'll stay logged in on this phone." }),
  ]);

  root.appendChild(el);

  /* -------------------------------------------------------------- the list */

  function skeletonPills(): void {
    clear(pills);
    for (let i = 0; i < 5; i++) pills.appendChild(h('div', { class: 'login-skel' }));
  }

  function failed(message: string, retry: boolean): void {
    clear(pills);
    const again = h('button', { class: 'login-retry', type: 'button', text: 'Try again' });
    again.addEventListener('click', load);
    pills.appendChild(
      h('div', { class: 'login-fail', role: 'status' }, [
        h('p', { text: message }),
        retry ? again : null,
      ]),
    );
    if (retry) again.focus();
  }

  function paintPills(list: LoginMember[]): void {
    clear(pills);
    for (const m of list) {
      const pill = h('button', {
        class: 'login-pill',
        type: 'button',
        'data-id': m.id,
        style: `--pill:${m.color || 'var(--accent)'}`,
      }) as HTMLButtonElement;

      pill.append(
        h('span', { class: 'pill-chip', 'aria-hidden': 'true' }),
        h('span', { class: 'pill-who' }, [
          h('span', { class: 'pill-name', text: m.name }),
          m.instrument ? h('span', { class: 'pill-inst', text: m.instrument }) : null,
        ]),
      );
      // The screen reader hears the whole pill, not "Brad, guitar, button".
      pill.setAttribute(
        'aria-label',
        m.instrument ? `${m.name} — ${m.instrument}` : m.name,
      );
      pill.addEventListener('click', () => pick(m, pill));
      pills.appendChild(pill);
    }
  }

  async function load(): Promise<void> {
    skeletonPills();
    try {
      const list = asMembers(await callFirst<unknown>(MEMBER_PATHS, undefined, ac.signal));
      if (dead) return;
      if (list.length === 0) {
        failed("Nobody's on the list yet. Ask Jonathan.", false);
        return;
      }
      paintPills(list);
    } catch (e) {
      if (dead || ac.signal.aborted) return;
      failed(vaultMessage(e), true);
    }
  }

  /* ---------------------------------------------------------------- stages */

  function pick(m: LoginMember, pill: HTMLElement): void {
    picked = m;
    stage = 'pin';
    pin = '';

    // The colour follows you through the whole screen: the dots, the key
    // presses, the record.
    el.style.setProperty('--pin', m.color || 'var(--accent)');
    mark.setColor(m.color || '#d8241a');

    for (const p of pills.querySelectorAll<HTMLElement>('.login-pill')) {
      const me = p === pill;
      p.classList.toggle('is-me', me);
      if (me) p.setAttribute('aria-current', 'true');
      else p.removeAttribute('aria-current');
    }

    // The other four collapse; this one rises to the top. It's the same DOM,
    // so nothing can get out of step with what you tapped.
    el.dataset.stage = 'pin';
    pad.hidden = false;
    pad.setAttribute('aria-label', `Enter the PIN for ${m.name}`);
    say.textContent = `Hi ${m.name}. Enter the PIN.`;

    tell('');
    paintDots();
    paintKeys();
    if (lockedUntil > Date.now()) paintLock();

    // Land on the keypad, so a keyboard user is already there. On a phone this
    // pops nothing up: they are buttons, not inputs.
    window.setTimeout(() => {
      if (!dead && stage === 'pin') keys[0]?.focus();
    }, 80);
  }

  function unpick(): void {
    if (busy) return;
    stage = 'pick';
    pin = '';
    pad.hidden = true;
    el.dataset.stage = 'pick';
    el.style.removeProperty('--pin');
    mark.setColor('#d8241a');
    say.textContent = 'Tap your name.';
    tell('');
    paintDots();

    const old = picked;
    picked = null;
    for (const p of pills.querySelectorAll<HTMLElement>('.login-pill')) {
      p.classList.remove('is-me');
      p.removeAttribute('aria-current');
    }
    // Put focus back where the finger was, not at the top of the page.
    if (old) pills.querySelector<HTMLElement>(`.login-pill[data-id="${CSS.escape(old.id)}"]`)?.focus();
  }

  /* ------------------------------------------------------------- the digits */

  const locked = (): boolean => Date.now() < lockedUntil;

  function paintDots(): void {
    dotEls.forEach((d, i) => d.classList.toggle('on', i < pin.length));
    dots.setAttribute('aria-label', `${pin.length} of ${PIN_LENGTH} digits entered`);
  }

  function paintKeys(): void {
    const off = busy || locked();
    for (const k of keys) k.disabled = off;
    pad.dataset.busy = busy ? '1' : '0';
  }

  /** Writes the one line of feedback. Never twice — an aria-live region that
   *  re-announces the same sentence every second is a nightmare on a phone. */
  function tell(text: string, bad = false): void {
    if (msg.textContent === text) {
      msg.dataset.bad = bad ? '1' : '0';
      return;
    }
    msg.textContent = text;
    msg.dataset.bad = bad ? '1' : '0';
  }

  function press(d: string): void {
    if (busy || locked() || pin.length >= PIN_LENGTH) return;
    if (msg.dataset.bad === '1') tell(''); // start typing, the telling-off goes away
    pin += d;
    paintDots();
    if (pin.length === PIN_LENGTH) void submit();
  }

  function rub(): void {
    if (busy || locked() || pin.length === 0) return;
    pin = pin.slice(0, -1);
    paintDots();
  }

  function shake(): void {
    dots.classList.remove('bad');
    // Reflow, or the class goes on and off inside one frame and nothing moves.
    void dots.offsetWidth;
    dots.classList.add('bad');
  }

  function wrong(): void {
    pin = '';
    paintDots();
    if (!REDUCED()) shake();
    buzz(90);
    tell("That's not it. Try again.", true);
  }

  /** 429. The Worker knows how long; we say it in minutes and we lock the pad,
   *  because a keypad that silently refuses every digit looks broken. */
  function paintLock(): void {
    const left = Math.ceil((lockedUntil - Date.now()) / 1000);
    if (left <= 0) {
      lockedUntil = 0;
      window.clearInterval(lockTimer);
      lockTimer = 0;
      tell('Have another go.');
      paintKeys();
      return;
    }
    tell(`Too many tries. Wait ${minutesFrom(left)}.`, true);
    paintKeys();
  }

  function lock(seconds: number | null): void {
    pin = '';
    paintDots();
    buzz(90);

    if (!seconds || seconds <= 0) {
      // No number from the Worker: say the true, vague thing rather than a lie.
      tell('Too many tries. Wait a few minutes.', true);
      paintKeys();
      return;
    }

    lockedUntil = Date.now() + Math.min(seconds, 3600) * 1000;
    window.clearInterval(lockTimer);
    lockTimer = window.setInterval(paintLock, 1000);
    paintLock();
  }

  function succeed(me: LoginMember): void {
    if (done || dead) return;
    done = true;
    tell('In you go.');
    el.dataset.ok = '1';
    const hand = () => {
      if (dead) return;
      // Belt and braces. destroy() takes this class off too, but if whoever
      // owns the router ever swaps the screen without calling our teardown, a
      // stuck `is-login` means an app with no tab bar and no transport, forever.
      // The door is open; the class comes off. Both paths, every time.
      document.body.classList.remove('is-login');
      onDone(me);
    };
    // A beat of the record spinning up, and then the shelf. Not on a phone that
    // has asked for less motion.
    if (REDUCED()) hand();
    else okTimer = window.setTimeout(hand, 260);
  }

  async function submit(): Promise<void> {
    if (!picked || busy) return;
    busy = true;
    paintKeys();
    tell('Checking…');

    try {
      const res = await callFirst<unknown>(
        LOGIN_PATHS,
        { member_id: picked.id, pin },
        ac.signal,
      );
      if (dead) return;
      // The Worker is the authority on who you are; fall back to the pill only
      // if it answered 200 with nothing useful.
      succeed(asMember(res) ?? picked);
    } catch (e) {
      if (dead || ac.signal.aborted) return;
      busy = false;

      const status = e instanceof LoginError ? e.status : -1;
      if (status === 429) {
        lock(e instanceof LoginError ? e.retryAfterS : null);
      } else if (status === 401 || status === 403 || status === 400) {
        // The only thing a wrong PIN is ever told. No codes, no counts, no
        // "3 attempts remaining" — that just teaches you to panic.
        wrong();
      } else {
        pin = '';
        paintDots();
        tell(vaultMessage(e), true);
      }
      paintKeys();
    }
  }

  /* ------------------------------------------------------ a real keyboard */

  function onKey(e: KeyboardEvent): void {
    if (dead || stage !== 'pin') return;
    if (e.key.length === 1 && e.key >= '0' && e.key <= '9') {
      press(e.key);
    } else if (e.key === 'Backspace') {
      e.preventDefault(); // or Safari treats it as Back
      rub();
    } else if (e.key === 'Escape') {
      unpick();
    }
  }
  document.addEventListener('keydown', onKey);

  void load();

  /* ------------------------------------------------------------- teardown */

  return function destroy(): void {
    dead = true;
    ac.abort();
    document.removeEventListener('keydown', onKey);
    window.clearInterval(lockTimer);
    window.clearTimeout(okTimer);
    mark.destroy();
    document.body.classList.remove('is-login');
    clear(root);
  };
}
